// src/modules/customers/components/CustomerCasesSection.jsx
//
// Every pledge and walk-in case this customer has, newest first, with a few
// key figures per case. Each row opens the case. Uses the normal case list
// endpoints (customerId filter), so the viewer's branch scoping still applies.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Segmented, Space, Tag, Typography, Spin, Empty, Button, Alert } from 'antd';
import { ProfileOutlined, RightOutlined, CalendarOutlined, BankOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchPledgeReleaseCases } from '../../pledgeReleaseCase/api';
import { fetchWalkInCases } from '../../walkInCase/api';
import { usePermissions } from '../../../hooks/usePermissions';

const { Text } = Typography;
const PAGE = 20;

// Same colours as the case list pages.
const PLEDGE_STATUS_COLORS = {
  LEAD_IDENTIFIED: 'blue', DOCS_VERIFIED: 'cyan', ADVANCE_APPROVED: 'geekblue', PART_PAYMENT_DONE: 'orange',
  FULLY_PAID_AWAITING_RELEASE: 'gold', GOLD_RECEIVED: 'lime', VALUATION_COMPLETED: 'purple',
  PAYMENT_SETTLED: 'volcano', BILL_GENERATED: 'magenta', CLOSED: 'green',
};
const WALKIN_STATUS_COLORS = {
  NEW: 'default', EVALUATED: 'blue', PAYMENT_SETTLED: 'purple', BILL_GENERATED: 'cyan',
  IN_INVENTORY: 'gold', DISPATCHED: 'orange', SETTLED: 'green', CLOSED: 'lime',
};

const inr = (v) => (v || v === 0
  ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
  : '—');
const grams = (v) => (v ? `${Number(v).toFixed(3)} g` : '—');
const pretty = (s = '') => s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
const paymentModeLabel = (m) => ({ CASH: 'Cash', BANK_TRANSFER: 'Bank Transfer', UPI: 'UPI' }[m] || m || '—');

// The few figures shown under each case.
const pledgeFacts = (c) => [
  { label: 'Lender', value: c.lenderName || '—' },
  { label: 'Net Wt', value: grams(c.finalTotalNetWeight || c.totalNetWeight) },
  { label: 'Closure', value: inr(c.closureAmount) },
  { label: 'Net Value', value: inr(c.finalNetValue) },
];
const walkInFacts = (c) => [
  { label: 'Net Wt', value: grams(c.totalNetWeight) },
  { label: 'Payout', value: inr(c.finalPaymentToCustomer) },
  { label: 'Payment', value: paymentModeLabel(c.paymentMode) },
];

const KINDS = {
  pledge: {
    label: 'Pledge',
    fetch: fetchPledgeReleaseCases,
    path: (c) => `/pledge-release-cases/${c._id}`,
    colors: PLEDGE_STATUS_COLORS,
    facts: pledgeFacts,
  },
  walkIn: {
    label: 'Walk-in',
    fetch: fetchWalkInCases,
    path: (c) => `/walk-in-cases/${c._id}`,
    colors: WALKIN_STATUS_COLORS,
    facts: walkInFacts,
  },
};

function useCases(kind, customerId, enabled) {
  const [state, setState] = useState({ items: [], total: 0, page: 0, loading: enabled, error: null });

  const load = useCallback(async (page) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await KINDS[kind].fetch({ customerId, page, limit: PAGE });
      setState((s) => ({
        items: page === 1 ? res.data || [] : [...s.items, ...(res.data || [])],
        total: res.meta?.total ?? 0,
        page,
        loading: false,
        error: null,
      }));
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err?.response?.data?.message || 'Could not load cases' }));
    }
  }, [kind, customerId]);

  useEffect(() => { if (enabled && customerId) load(1); }, [enabled, customerId, load]);
  return { ...state, loadMore: () => load(state.page + 1) };
}

function CaseRow({ c, kind, onOpen }) {
  const cfg = KINDS[kind];
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      className="customer-case-row"
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: '10px 12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Line 1 — identity + status */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          <Text strong style={{ color: '#1677ff' }}>{c.caseId || '—'}</Text>
          {c.metalType === 'SILVER' && <Tag style={{ margin: 0 }}>Silver</Tag>}
          <Tag color={cfg.colors[c.status] || 'default'} style={{ margin: 0 }}>{pretty(c.status)}</Tag>
          {c.isDeleted && <Tag color="red" style={{ margin: 0 }}>Deleted</Tag>}
        </div>

        {/* Line 2 — when / where */}
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 14, rowGap: 2, marginTop: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <CalendarOutlined /> {c.createdAt ? dayjs(c.createdAt).format('DD MMM YYYY') : '—'}
          </Text>
          {c.branch?.name && (
            <Text type="secondary" style={{ fontSize: 12 }}><BankOutlined /> {c.branch.name}</Text>
          )}
        </div>

        {/* Line 3 — key figures; wraps into a 2-column grid on phones */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
            gap: '4px 16px',
            marginTop: 8,
          }}
        >
          {cfg.facts(c).map((f) => (
            <div key={f.label} style={{ minWidth: 0 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{f.label}</Text>
              <Text style={{ fontSize: 13 }} ellipsis={{ tooltip: f.value }}>{f.value}</Text>
            </div>
          ))}
        </div>
      </div>
      <RightOutlined style={{ color: '#bfbfbf', flexShrink: 0 }} />
    </div>
  );
}

const CustomerCasesSection = ({ customerId }) => {
  const navigate = useNavigate();
  const { canViewModule } = usePermissions();
  const canPledge = canViewModule('pledgeReleaseCase');
  const canWalkIn = canViewModule('walkInCase');

  const pledge = useCases('pledge', customerId, canPledge);
  const walkIn = useCases('walkIn', customerId, canWalkIn);
  const [tab, setTab] = useState(canPledge ? 'pledge' : 'walkIn');

  if (!canPledge && !canWalkIn) return null;

  const current = tab === 'pledge' ? pledge : walkIn;
  const options = [
    canPledge && { value: 'pledge', label: `Pledge (${pledge.total})` },
    canWalkIn && { value: 'walkIn', label: `Walk-in (${walkIn.total})` },
  ].filter(Boolean);

  return (
    <Card
      size="small"
      title={<Space><ProfileOutlined /><span>Cases</span></Space>}
      extra={options.length > 1 && <Segmented size="small" value={tab} onChange={setTab} options={options} />}
    >
      <style>{`.customer-case-row:hover { border-color: #91caff !important; background: #f5faff; }`}</style>
      {current.error && <Alert type="error" showIcon message={current.error} style={{ marginBottom: 12 }} />}
      {current.loading && current.items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      ) : current.items.length === 0 && !current.error ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${KINDS[tab].label.toLowerCase()} cases for this customer`} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {current.items.map((c) => (
            <CaseRow key={c._id} c={c} kind={tab} onOpen={() => navigate(KINDS[tab].path(c))} />
          ))}
          {current.items.length < current.total && (
            <Button block loading={current.loading} onClick={current.loadMore}>
              Show more ({current.total - current.items.length} left)
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

export default CustomerCasesSection;
